import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminPage.css';

const ADMIN_PASSWORD = 'donggukfc2026'; // 실제 운영 시 환경변수로 관리 권장

function AdminPage({ isAdmin, setIsAdmin, onBack }) {
  const [activeTab, setActiveTab] = useState('matches'); // 'matches', 'donations', 'players', or 'events'
  const [password, setPassword] = useState('');
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [donations, setDonations] = useState([]);
  const [events, setEvents] = useState([]);

  // 새 선수 추가
  const [newPlayerName, setNewPlayerName] = useState('');

  // 선수 수정
  const [editingPlayer, setEditingPlayer] = useState(null);

  // 새 경기 추가
  const [newMatchDate, setNewMatchDate] = useState('');
  const [newMatchOpponent, setNewMatchOpponent] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState({}); // { playerId: 'present' | 'late' | 'absent' | 'no-show' }
  const [quarters, setQuarters] = useState([
    { quarterNumber: 1, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
    { quarterNumber: 2, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
    { quarterNumber: 3, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
    { quarterNumber: 4, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 }
  ]);

  // 경기 수정
  const [editingMatch, setEditingMatch] = useState(null);

  // 새 찬조 추가
  const [donationDate, setDonationDate] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [contributors, setContributors] = useState([{ playerId: '', amount: '' }]);

  // 찬조 수정
  const [editingDonation, setEditingDonation] = useState(null);

  // 새 일정 추가
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventType, setNewEventType] = useState('rental');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventTime, setNewEventTime] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventMemo, setNewEventMemo] = useState('');

  // 일정 수정
  const [editingEvent, setEditingEvent] = useState(null);

  // 아코디언 상태
  const [isMatchFormOpen, setIsMatchFormOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchPlayers();
      fetchMatches();
      fetchDonations();
      fetchEvents();
    }
  }, [isAdmin]);

  // 선수 목록이 변경되면 출석 상태를 모두 '결석'으로 초기화
  useEffect(() => {
    if (players.length > 0 && !editingMatch) {
      const initialStatus = {};
      players.forEach(player => {
        initialStatus[player.id] = 'absent';
      });
      setAttendanceStatus(initialStatus);
    }
  }, [players, editingMatch]);

  const fetchPlayers = async () => {
    try {
      const playersSnapshot = await getDocs(collection(db, 'players'));
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // 가나다순 정렬
      playersData.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
      setPlayers(playersData);
    } catch (error) {
      console.error('선수 데이터 가져오기 오류:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const matchesSnapshot = await getDocs(collection(db, 'matches'));
      const matchesData = matchesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // 날짜 순 정렬 (최신순)
      matchesData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setMatches(matchesData);
    } catch (error) {
      console.error('경기 데이터 가져오기 오류:', error);
    }
  };

  const fetchDonations = async () => {
    try {
      const donationsSnapshot = await getDocs(collection(db, 'donations'));
      const donationsData = donationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // 날짜 순 정렬 (최신순)
      donationsData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDonations(donationsData);
    } catch (error) {
      console.error('찬조 데이터 가져오기 오류:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // 날짜 순 정렬 (최신순)
      eventsData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(eventsData);
    } catch (error) {
      console.error('일정 데이터 가져오기 오류:', error);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert('비밀번호가 틀렸습니다.');
      setPassword('');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!newPlayerName.trim()) return;

    try {
      await addDoc(collection(db, 'players'), {
        name: newPlayerName,
        createdAt: serverTimestamp()
      });
      alert('선수가 추가되었습니다!');
      setNewPlayerName('');
      fetchPlayers();
    } catch (error) {
      console.error('선수 추가 오류:', error);
      alert('선수 추가에 실패했습니다.');
    }
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    if (!newMatchDate || !newMatchOpponent.trim()) {
      alert('날짜와 상대팀을 입력해주세요.');
      return;
    }

    try {
      // 각 쿼터의 득점자 정보를 정리 (선수가 선택된 것만)
      const cleanedQuarters = quarters.map(quarter => ({
        ...quarter,
        ourGoals: quarter.ourGoals.filter(goal => goal.playerId && goal.count > 0)
      }));

      await addDoc(collection(db, 'matches'), {
        date: newMatchDate,
        opponent: newMatchOpponent,
        attendanceStatus: attendanceStatus,
        quarters: cleanedQuarters,
        createdAt: serverTimestamp()
      });

      alert('경기가 추가되었습니다!');
      setNewMatchDate('');
      setNewMatchOpponent('');
      // 모든 선수를 '결석'으로 초기화
      const initialStatus = {};
      players.forEach(player => {
        initialStatus[player.id] = 'absent';
      });
      setAttendanceStatus(initialStatus);
      setQuarters([
        { quarterNumber: 1, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 2, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 3, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 4, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 }
      ]);
      setIsMatchFormOpen(false); // 폼 닫기
      fetchMatches();
    } catch (error) {
      console.error('경기 추가 오류:', error);
      alert('경기 추가에 실패했습니다.');
    }
  };

  const handleDeletePlayer = async (playerId, playerName) => {
    if (!window.confirm(`${playerName} 선수를 삭제하시겠습니까?`)) return;

    try {
      await deleteDoc(doc(db, 'players', playerId));
      alert('선수가 삭제되었습니다.');
      fetchPlayers();
    } catch (error) {
      console.error('선수 삭제 오류:', error);
      alert('선수 삭제에 실패했습니다.');
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setNewPlayerName(player.name);
  };

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();
    if (!newPlayerName.trim()) return;

    try {
      await updateDoc(doc(db, 'players', editingPlayer.id), {
        name: newPlayerName
      });
      alert('선수 이름이 수정되었습니다!');
      setEditingPlayer(null);
      setNewPlayerName('');
      fetchPlayers();
    } catch (error) {
      console.error('선수 수정 오류:', error);
      alert('선수 수정에 실패했습니다.');
    }
  };

  const cancelPlayerEdit = () => {
    setEditingPlayer(null);
    setNewPlayerName('');
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('이 경기를 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, 'matches', matchId));
      alert('경기가 삭제되었습니다.');
      fetchMatches();
    } catch (error) {
      console.error('경기 삭제 오류:', error);
      alert('경기 삭제에 실패했습니다.');
    }
  };

  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setNewMatchDate(match.date);
    setNewMatchOpponent(match.opponent);

    // 모든 선수를 기본값 '결석'으로 초기화
    const statusObj = {};
    players.forEach(player => {
      statusObj[player.id] = 'absent';
    });

    // 기존 데이터가 있으면 덮어쓰기
    if (match.attendanceStatus) {
      Object.keys(match.attendanceStatus).forEach(playerId => {
        statusObj[playerId] = match.attendanceStatus[playerId];
      });
    } else if (match.attendance) {
      // 이전 데이터 호환성
      match.attendance.forEach(playerId => {
        statusObj[playerId] = 'present';
      });
    }

    setAttendanceStatus(statusObj);

    // 쿼터 데이터 로드
    if (match.quarters) {
      // 기존 데이터에 opponentOwnGoals가 없으면 0으로 추가
      const updatedQuarters = match.quarters.map(q => ({
        ...q,
        opponentOwnGoals: q.opponentOwnGoals || 0
      }));
      setQuarters(updatedQuarters);
    } else {
      // 기존 데이터 호환성 (구 방식)
      const defaultQuarters = [
        { quarterNumber: 1, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 2, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 3, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 4, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 }
      ];
      // 기존 데이터에 opponentOwnGoals가 없으면 0으로 추가
      setQuarters(defaultQuarters);
    }
    setIsMatchFormOpen(true); // 수정 모드일 때 폼 자동으로 열기
  };

  const handleUpdateMatch = async (e) => {
    e.preventDefault();
    if (!newMatchDate || !newMatchOpponent.trim()) {
      alert('날짜와 상대팀을 입력해주세요.');
      return;
    }

    try {
      // 각 쿼터의 득점자 정보를 정리 (선수가 선택된 것만)
      const cleanedQuarters = quarters.map(quarter => ({
        ...quarter,
        ourGoals: quarter.ourGoals.filter(goal => goal.playerId && goal.count > 0)
      }));

      await updateDoc(doc(db, 'matches', editingMatch.id), {
        date: newMatchDate,
        opponent: newMatchOpponent,
        attendanceStatus: attendanceStatus,
        quarters: cleanedQuarters
      });

      alert('경기가 수정되었습니다!');
      setEditingMatch(null);
      setNewMatchDate('');
      setNewMatchOpponent('');
      // 모든 선수를 '결석'으로 초기화
      const initialStatus = {};
      players.forEach(player => {
        initialStatus[player.id] = 'absent';
      });
      setAttendanceStatus(initialStatus);
      setQuarters([
        { quarterNumber: 1, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 2, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 3, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 },
        { quarterNumber: 4, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 }
      ]);
      setIsMatchFormOpen(false); // 폼 닫기
      fetchMatches();
    } catch (error) {
      console.error('경기 수정 오류:', error);
      alert('경기 수정에 실패했습니다.');
    }
  };

  const cancelEdit = () => {
    setEditingMatch(null);
    setNewMatchDate('');
    setNewMatchOpponent('');
    // 모든 선수를 '결석'으로 초기화
    const initialStatus = {};
    players.forEach(player => {
      initialStatus[player.id] = 'absent';
    });
    setAttendanceStatus(initialStatus);
    setQuarters([
      { quarterNumber: 1, ourGoals: [], opponentGoals: 0 },
      { quarterNumber: 2, ourGoals: [], opponentGoals: 0 },
      { quarterNumber: 3, ourGoals: [], opponentGoals: 0 },
      { quarterNumber: 4, ourGoals: [], opponentGoals: 0 }
    ]);
    setIsMatchFormOpen(false); // 폼 닫기
  };

  const handleAttendanceChange = (playerId, status) => {
    setAttendanceStatus(prev => ({
      ...prev,
      [playerId]: status
    }));
  };

  // 쿼터 추가
  const addQuarter = () => {
    const newQuarterNumber = quarters.length + 1;
    setQuarters([...quarters, { quarterNumber: newQuarterNumber, ourGoals: [], opponentGoals: 0, opponentOwnGoals: 0 }]);
  };

  // 쿼터 삭제
  const removeQuarter = (index) => {
    if (quarters.length <= 4) {
      alert('기본 4쿼터는 삭제할 수 없습니다.');
      return;
    }
    const updated = quarters.filter((_, i) => i !== index);
    setQuarters(updated);
  };

  // 득점자 추가 (특정 쿼터에)
  const addGoalScorer = (quarterIndex) => {
    const updated = [...quarters];
    updated[quarterIndex].ourGoals.push({ playerId: '', count: 1 });
    setQuarters(updated);
  };

  // 득점자 삭제
  const removeGoalScorer = (quarterIndex, goalIndex) => {
    const updated = [...quarters];
    updated[quarterIndex].ourGoals = updated[quarterIndex].ourGoals.filter((_, i) => i !== goalIndex);
    setQuarters(updated);
  };

  // 득점자 정보 변경
  const handleGoalScorerChange = (quarterIndex, goalIndex, field, value) => {
    const updated = [...quarters];
    updated[quarterIndex].ourGoals[goalIndex][field] = field === 'count' ? parseInt(value) || 1 : value;
    setQuarters(updated);
  };

  // 상대팀 득점 변경
  const handleOpponentGoalsChange = (quarterIndex, value) => {
    const updated = [...quarters];
    updated[quarterIndex].opponentGoals = parseInt(value) || 0;
    setQuarters(updated);
  };

  // 상대팀 자살골 변경
  const handleOpponentOwnGoalsChange = (quarterIndex, value) => {
    const updated = [...quarters];
    updated[quarterIndex].opponentOwnGoals = parseInt(value) || 0;
    setQuarters(updated);
  };

  if (!isAdmin) {
    return (
      <div className="admin-login">
        <h2>관리자 로그인</h2>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">로그인</button>
        </form>
      </div>
    );
  }

  // 찬조자 추가
  const addContributor = () => {
    setContributors([...contributors, { playerId: '', amount: '' }]);
  };

  // 찬조자 제거
  const removeContributor = (index) => {
    if (contributors.length === 1) {
      alert('최소 1명의 찬조자가 필요합니다.');
      return;
    }
    setContributors(contributors.filter((_, i) => i !== index));
  };

  // 찬조자 정보 변경
  const handleContributorChange = (index, field, value) => {
    const updated = [...contributors];
    updated[index][field] = value;
    setContributors(updated);
  };

  // 찬조 추가
  const handleAddDonation = async (e) => {
    e.preventDefault();
    if (!donationDate || !businessName.trim()) {
      alert('날짜와 상호명을 입력해주세요.');
      return;
    }

    // 모든 찬조자가 선택되고 금액이 입력되었는지 확인
    const validContributors = contributors.filter(c => c.playerId && c.amount);
    if (validContributors.length === 0) {
      alert('최소 1명의 찬조자와 금액을 입력해주세요.');
      return;
    }

    try {
      const totalAmount = validContributors.reduce((sum, c) => sum + parseInt(c.amount), 0);

      await addDoc(collection(db, 'donations'), {
        date: donationDate,
        businessName: businessName,
        totalAmount: totalAmount,
        contributors: validContributors.map(c => ({
          playerId: c.playerId,
          amount: parseInt(c.amount)
        })),
        createdAt: serverTimestamp()
      });

      alert('찬조가 추가되었습니다!');
      setDonationDate('');
      setBusinessName('');
      setContributors([{ playerId: '', amount: '' }]);
      fetchDonations();
    } catch (error) {
      console.error('찬조 추가 오류:', error);
      alert('찬조 추가에 실패했습니다.');
    }
  };

  // 찬조 삭제
  const handleDeleteDonation = async (donationId) => {
    if (!window.confirm('이 찬조 기록을 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, 'donations', donationId));
      alert('찬조 기록이 삭제되었습니다.');
      fetchDonations();
    } catch (error) {
      console.error('찬조 삭제 오류:', error);
      alert('찬조 삭제에 실패했습니다.');
    }
  };

  // 찬조 수정
  const handleEditDonation = (donation) => {
    setEditingDonation(donation);
    setDonationDate(donation.date);
    setBusinessName(donation.businessName);
    setContributors(donation.contributors.map(c => ({ ...c, amount: c.amount.toString() })));
  };

  // 찬조 수정 완료
  const handleUpdateDonation = async (e) => {
    e.preventDefault();
    if (!donationDate || !businessName.trim()) {
      alert('날짜와 상호명을 입력해주세요.');
      return;
    }

    const validContributors = contributors.filter(c => c.playerId && c.amount);
    if (validContributors.length === 0) {
      alert('최소 1명의 찬조자와 금액을 입력해주세요.');
      return;
    }

    try {
      const totalAmount = validContributors.reduce((sum, c) => sum + parseInt(c.amount), 0);

      await updateDoc(doc(db, 'donations', editingDonation.id), {
        date: donationDate,
        businessName: businessName,
        totalAmount: totalAmount,
        contributors: validContributors.map(c => ({
          playerId: c.playerId,
          amount: parseInt(c.amount)
        }))
      });

      alert('찬조가 수정되었습니다!');
      setEditingDonation(null);
      setDonationDate('');
      setBusinessName('');
      setContributors([{ playerId: '', amount: '' }]);
      fetchDonations();
    } catch (error) {
      console.error('찬조 수정 오류:', error);
      alert('찬조 수정에 실패했습니다.');
    }
  };

  // 찬조 수정 취소
  const cancelDonationEdit = () => {
    setEditingDonation(null);
    setDonationDate('');
    setBusinessName('');
    setContributors([{ playerId: '', amount: '' }]);
  };

  // 일정 추가
  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEventDate || !newEventTitle.trim()) {
      alert('날짜와 제목을 입력해주세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'events'), {
        date: newEventDate,
        type: newEventType,
        title: newEventTitle,
        time: newEventTime || '',
        location: newEventLocation || '',
        memo: newEventMemo || '',
        createdAt: serverTimestamp()
      });

      alert('일정이 추가되었습니다!');
      setNewEventDate('');
      setNewEventType('rental');
      setNewEventTitle('');
      setNewEventTime('');
      setNewEventLocation('');
      setNewEventMemo('');
      fetchEvents();
    } catch (error) {
      console.error('일정 추가 오류:', error);
      alert('일정 추가에 실패했습니다.');
    }
  };

  // 일정 삭제
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, 'events', eventId));
      alert('일정이 삭제되었습니다.');
      fetchEvents();
    } catch (error) {
      console.error('일정 삭제 오류:', error);
      alert('일정 삭제에 실패했습니다.');
    }
  };

  // 일정 수정
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setNewEventDate(event.date);
    setNewEventType(event.type);
    setNewEventTitle(event.title);
    setNewEventTime(event.time || '');
    setNewEventLocation(event.location || '');
    setNewEventMemo(event.memo || '');
  };

  // 일정 업데이트
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!newEventDate || !newEventTitle.trim()) {
      alert('날짜와 제목을 입력해주세요.');
      return;
    }

    try {
      await updateDoc(doc(db, 'events', editingEvent.id), {
        date: newEventDate,
        type: newEventType,
        title: newEventTitle,
        time: newEventTime || '',
        location: newEventLocation || '',
        memo: newEventMemo || ''
      });

      alert('일정이 수정되었습니다!');
      setEditingEvent(null);
      setNewEventDate('');
      setNewEventType('rental');
      setNewEventTitle('');
      setNewEventTime('');
      setNewEventLocation('');
      setNewEventMemo('');
      fetchEvents();
    } catch (error) {
      console.error('일정 수정 오류:', error);
      alert('일정 수정에 실패했습니다.');
    }
  };

  // 일정 수정 취소
  const cancelEventEdit = () => {
    setEditingEvent(null);
    setNewEventDate('');
    setNewEventType('rental');
    setNewEventTitle('');
    setNewEventTime('');
    setNewEventLocation('');
    setNewEventMemo('');
  };

  // 일정 타입별 라벨
  const getEventTypeLabel = (type) => {
    const labels = {
      'rental': '대관',
      'no-rental': '대관없음',
      'event': '행사',
      'tournament': '대회',
      'family-event': '경조사',
      'holiday': '연휴'
    };
    return labels[type] || type;
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : '알 수 없음';
  };

  const getAttendanceStatusCounts = (match) => {
    const counts = {
      present: 0,
      late: 0,
      absent: 0,
      noShow: 0
    };

    if (match.attendanceStatus) {
      Object.values(match.attendanceStatus).forEach(status => {
        if (status === 'present') counts.present++;
        else if (status === 'late') counts.late++;
        else if (status === 'absent') counts.absent++;
        else if (status === 'no-show') counts.noShow++;
      });
    } else if (match.attendance) {
      // 기존 데이터는 모두 출석으로 간주
      counts.present = match.attendance.length;
    }

    return counts;
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>관리자 페이지</h2>
        <div className="admin-actions">
          {onBack && <button onClick={onBack} className="back-button">돌아가기</button>}
          <button onClick={() => setIsAdmin(false)} className="logout-button-small">
            로그아웃
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'events' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('events')}
        >
          일정 관리
        </button>
        <button
          className={activeTab === 'matches' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('matches')}
        >
          경기 관리
        </button>
        <button
          className={activeTab === 'donations' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('donations')}
        >
          찬조 기록
        </button>
        <button
          className={activeTab === 'players' ? 'tab-button active' : 'tab-button'}
          onClick={() => setActiveTab('players')}
        >
          선수 관리
        </button>
      </div>

      {activeTab === 'events' && (
        <div className="tab-content">
          <section className="admin-section">
            <h3>{editingEvent ? '일정 수정' : '새 일정 추가'}</h3>
            <form onSubmit={editingEvent ? handleUpdateEvent : handleAddEvent} className="event-form">
              <div className="form-group">
                <label htmlFor="eventDate">날짜 *</label>
                <input
                  type="date"
                  id="eventDate"
                  value={newEventDate}
                  onChange={(e) => setNewEventDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventType">일정 타입 *</label>
                <select
                  id="eventType"
                  value={newEventType}
                  onChange={(e) => setNewEventType(e.target.value)}
                  required
                >
                  <option value="rental">대관</option>
                  <option value="no-rental">대관없음</option>
                  <option value="event">행사</option>
                  <option value="tournament">대회</option>
                  <option value="family-event">경조사</option>
                  <option value="holiday">연휴</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="eventTitle">제목 *</label>
                <input
                  type="text"
                  id="eventTitle"
                  value={newEventTitle}
                  onChange={(e) => setNewEventTitle(e.target.value)}
                  placeholder="일정 제목을 입력하세요"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventTime">시간</label>
                <input
                  type="time"
                  id="eventTime"
                  value={newEventTime}
                  onChange={(e) => setNewEventTime(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventLocation">장소</label>
                <input
                  type="text"
                  id="eventLocation"
                  value={newEventLocation}
                  onChange={(e) => setNewEventLocation(e.target.value)}
                  placeholder="장소를 입력하세요"
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventMemo">메모</label>
                <textarea
                  id="eventMemo"
                  value={newEventMemo}
                  onChange={(e) => setNewEventMemo(e.target.value)}
                  placeholder="추가 메모를 입력하세요"
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {editingEvent ? '수정' : '추가'}
                </button>
                {editingEvent && (
                  <button type="button" className="cancel-btn" onClick={cancelEventEdit}>
                    취소
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="admin-section">
            <h3>일정 목록</h3>
            {events.length === 0 ? (
              <p className="no-data">아직 등록된 일정이 없습니다.</p>
            ) : (
              <div className="events-list">
                {events.map((event) => (
                  <div key={event.id} className="event-card">
                    <div className="event-header">
                      <div className="event-date-type">
                        <span className="event-date">{event.date}</span>
                        <span className={`event-type-badge ${event.type}`}>
                          {getEventTypeLabel(event.type)}
                        </span>
                      </div>
                      <div className="event-actions">
                        <button onClick={() => handleEditEvent(event)} className="edit-btn">
                          수정
                        </button>
                        <button onClick={() => handleDeleteEvent(event.id)} className="delete-btn">
                          삭제
                        </button>
                      </div>
                    </div>
                    <div className="event-details">
                      <h4>{event.title}</h4>
                      {event.time && <p>⏰ {event.time}</p>}
                      {event.location && <p>📍 {event.location}</p>}
                      {event.memo && <p className="event-memo-text">{event.memo}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="tab-content">
          <section className="admin-section">
            <h3>{editingPlayer ? '선수 이름 수정' : '새 선수 추가'}</h3>
            <form onSubmit={editingPlayer ? handleUpdatePlayer : handleAddPlayer} className="add-player-form">
              <input
                type="text"
                placeholder="선수 이름"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
              />
              <div className="form-buttons-inline">
                <button type="submit">{editingPlayer ? '수정' : '추가'}</button>
                {editingPlayer && (
                  <button type="button" onClick={cancelPlayerEdit} className="cancel-button-small">
                    취소
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="admin-section">
            <h3>선수 목록</h3>
            {players.length === 0 ? (
              <p className="no-data">등록된 선수가 없습니다.</p>
            ) : (
              <div className="players-list">
                {players.map(player => (
                  <div key={player.id} className="player-item">
                    <span className="player-item-name">{player.name}</span>
                    <div className="player-actions">
                      <button
                        onClick={() => handleEditPlayer(player)}
                        className="edit-button"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeletePlayer(player.id, player.name)}
                        className="delete-button"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'matches' && (
        <div className="tab-content">
          <section className="admin-section">
            <h3
              onClick={() => setIsMatchFormOpen(!isMatchFormOpen)}
              className="accordion-header"
              style={{ cursor: 'pointer' }}
            >
              {editingMatch ? '경기 수정' : '새 경기 추가'} {isMatchFormOpen ? '▲' : '▼'}
            </h3>
            {isMatchFormOpen && (
              <form onSubmit={editingMatch ? handleUpdateMatch : handleAddMatch} className="add-match-form">
              <div className="form-group">
                <label>경기 날짜:</label>
                <input
                  type="date"
                  value={newMatchDate}
                  onChange={(e) => setNewMatchDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>상대팀:</label>
                <input
                  type="text"
                  placeholder="상대팀 이름"
                  value={newMatchOpponent}
                  onChange={(e) => setNewMatchOpponent(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>출석 상태:</label>
                <div className="attendance-status-list">
                  {players.map(player => (
                    <div key={player.id} className="attendance-status-item">
                      <div className="player-name-label">{player.name}</div>
                      <div className="status-radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name={`attendance-${player.id}`}
                            value="present"
                            checked={attendanceStatus[player.id] === 'present'}
                            onChange={() => handleAttendanceChange(player.id, 'present')}
                          />
                          <span>출석</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name={`attendance-${player.id}`}
                            value="late"
                            checked={attendanceStatus[player.id] === 'late'}
                            onChange={() => handleAttendanceChange(player.id, 'late')}
                          />
                          <span>지각</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name={`attendance-${player.id}`}
                            value="absent"
                            checked={attendanceStatus[player.id] === 'absent'}
                            onChange={() => handleAttendanceChange(player.id, 'absent')}
                          />
                          <span>불참</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name={`attendance-${player.id}`}
                            value="no-show"
                            checked={attendanceStatus[player.id] === 'no-show'}
                            onChange={() => handleAttendanceChange(player.id, 'no-show')}
                          />
                          <span>무단불참</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>쿼터별 득점:</label>
                <div className="quarters-list">
                  {quarters.map((quarter, qIndex) => (
                    <div key={qIndex} className="quarter-section">
                      <div className="quarter-header">
                        <h4>{quarter.quarterNumber}쿼터</h4>
                        {quarters.length > 4 && (
                          <button
                            type="button"
                            onClick={() => removeQuarter(qIndex)}
                            className="remove-quarter-button"
                          >
                            쿼터 삭제
                          </button>
                        )}
                      </div>

                      <div className="quarter-goals">
                        <div className="our-goals-section">
                          <label>우리팀 득점:</label>
                          {quarter.ourGoals.map((goal, gIndex) => (
                            <div key={gIndex} className="goal-scorer-item">
                              <select
                                value={goal.playerId}
                                onChange={(e) => handleGoalScorerChange(qIndex, gIndex, 'playerId', e.target.value)}
                                className="scorer-select"
                              >
                                <option value="">선수 선택</option>
                                {players.map(player => (
                                  <option key={player.id} value={player.id}>{player.name}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min="1"
                                value={goal.count}
                                onChange={(e) => handleGoalScorerChange(qIndex, gIndex, 'count', e.target.value)}
                                className="goal-count-input"
                                placeholder="골 수"
                              />
                              <button
                                type="button"
                                onClick={() => removeGoalScorer(qIndex, gIndex)}
                                className="remove-goal-button"
                              >
                                삭제
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addGoalScorer(qIndex)}
                            className="add-goal-button"
                          >
                            + 득점자 추가
                          </button>
                        </div>

                        <div className="opponent-goals-section">
                          <label>상대팀 득점:</label>
                          <input
                            type="number"
                            min="0"
                            value={quarter.opponentGoals}
                            onChange={(e) => handleOpponentGoalsChange(qIndex, e.target.value)}
                            className="opponent-goals-input"
                            placeholder="0"
                          />
                        </div>

                        <div className="opponent-own-goals-section">
                          <label>상대팀 자살골 (OG):</label>
                          <input
                            type="number"
                            min="0"
                            value={quarter.opponentOwnGoals}
                            onChange={(e) => handleOpponentOwnGoalsChange(qIndex, e.target.value)}
                            className="opponent-own-goals-input"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addQuarter}
                    className="add-quarter-button"
                  >
                    + 쿼터 추가 (연장전)
                  </button>
                </div>
              </div>

              <div className="form-buttons">
                <button type="submit" className="submit-button">
                  {editingMatch ? '수정 완료' : '경기 추가'}
                </button>
                {editingMatch && (
                  <button type="button" onClick={cancelEdit} className="cancel-button">
                    취소
                  </button>
                )}
              </div>
            </form>
            )}
          </section>

          <section className="admin-section">
            <h3>경기 목록</h3>
            {matches.length === 0 ? (
              <p className="no-data">등록된 경기가 없습니다.</p>
            ) : (
              <div className="matches-list">
                {matches.map(match => (
                  <div key={match.id} className="match-item">
                    <div className="match-info">
                      <div className="match-date">{match.date}</div>
                      <div className="match-opponent">vs {match.opponent}</div>
                      <div className="match-details">
                        {(() => {
                          const statusCounts = getAttendanceStatusCounts(match);
                          return (
                            <>
                              <span>출석: {statusCounts.present}명</span>
                              <span> | 지각: {statusCounts.late}명</span>
                              <span> | 불참: {statusCounts.absent}명</span>
                              <span> | 무단불참: {statusCounts.noShow}명</span>
                            </>
                          );
                        })()}
                        {match.quarters && match.quarters.length > 0 && (
                          <>
                            <div className="total-score-summary">
                              <strong>총 스코어: {match.quarters.reduce((total, q) => total + q.ourGoals.reduce((sum, g) => sum + g.count, 0) + (q.opponentOwnGoals || 0), 0)} - {match.quarters.reduce((total, q) => total + q.opponentGoals, 0)}</strong>
                            </div>
                            <div className="quarters-summary">
                              {match.quarters.map((quarter, idx) => {
                                const ourGoals = quarter.ourGoals.reduce((sum, g) => sum + g.count, 0) + (quarter.opponentOwnGoals || 0);
                                return (
                                  <div key={idx}>
                                    {quarter.quarterNumber}쿼터: {ourGoals}-{quarter.opponentGoals}
                                    {(quarter.ourGoals.length > 0 || (quarter.opponentOwnGoals && quarter.opponentOwnGoals > 0)) && (
                                      <span> (
                                        {quarter.ourGoals.map(g => `${getPlayerName(g.playerId)} ${g.count > 1 ? g.count + '골' : ''}`).join(', ')}
                                        {quarter.ourGoals.length > 0 && quarter.opponentOwnGoals > 0 && ', '}
                                        {quarter.opponentOwnGoals > 0 && `OG ${quarter.opponentOwnGoals > 1 ? quarter.opponentOwnGoals + '골' : ''}`}
                                      )</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                        {/* 기존 데이터 호환성 */}
                        {match.goals && match.goals.length > 0 && !match.quarters && (
                          <span> | 득점: {match.goals.map(g => `${getPlayerName(g.playerId)} ${g.count}골`).join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="match-actions">
                      <button
                        onClick={() => handleEditMatch(match)}
                        className="edit-button"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteMatch(match.id)}
                        className="delete-button"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'donations' && (
        <div className="tab-content">
          <section className="admin-section">
            <h3>새 찬조 기록</h3>
            <form onSubmit={editingDonation ? handleUpdateDonation : handleAddDonation} className="add-match-form">
              <div className="form-group">
                <label>날짜:</label>
                <input
                  type="date"
                  value={donationDate}
                  onChange={(e) => setDonationDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>상호명:</label>
                <input
                  type="text"
                  placeholder="상호명 입력"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>찬조자:</label>
                <div className="contributors-list">
                  {contributors.map((contributor, index) => (
                    <div key={index} className="contributor-item">
                      <select
                        value={contributor.playerId}
                        onChange={(e) => handleContributorChange(index, 'playerId', e.target.value)}
                        className="contributor-select"
                      >
                        <option value="">선수 선택</option>
                        {players.map(player => (
                          <option key={player.id} value={player.id}>{player.name}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        placeholder="금액"
                        value={contributor.amount}
                        onChange={(e) => handleContributorChange(index, 'amount', e.target.value)}
                        className="contributor-amount"
                      />
                      {contributors.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContributor(index)}
                          className="remove-contributor-button"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addContributor}
                    className="add-contributor-button"
                  >
                    + 찬조자 추가
                  </button>
                </div>
              </div>

              <div className="form-buttons">
                <button type="submit" className="submit-button">
                  {editingDonation ? '수정 완료' : '찬조 추가'}
                </button>
                {editingDonation && (
                  <button type="button" onClick={cancelDonationEdit} className="cancel-button">
                    취소
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="admin-section">
            <h3>찬조 목록</h3>
            {donations.length === 0 ? (
              <p className="no-data">등록된 찬조가 없습니다.</p>
            ) : (
              <div className="donations-list">
                {donations.map(donation => (
                  <div key={donation.id} className="donation-item">
                    <div className="donation-info">
                      <div className="donation-date">{donation.date}</div>
                      <div className="donation-business">{donation.businessName}</div>
                      <div className="donation-details">
                        <span>총액: {donation.totalAmount.toLocaleString()}원</span>
                        <span> | 찬조자: {donation.contributors.map(c =>
                          `${getPlayerName(c.playerId)} ${c.amount.toLocaleString()}원`
                        ).join(', ')}</span>
                      </div>
                    </div>
                    <div className="donation-actions">
                      <button
                        onClick={() => handleEditDonation(donation)}
                        className="edit-button"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteDonation(donation.id)}
                        className="delete-button"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
